import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { PermissionRoute } from './PermissionRoute';
import { GuestRoute } from './GuestRoute';
import { LazyWrapper } from './components/LazyWrapper';
import { PERMISSIONS } from '@/shared/config';

// Lazy load pages
const HomePage = lazy(() => import('@/pages/home').then((module) => ({ default: module.HomePage })));
const LoginPage = lazy(() => import('@/pages/login').then((module) => ({ default: module.LoginPage })));
const BrandPage = lazy(() => import('@/pages/brand').then((module) => ({ default: module.BrandPage })));
const BrandCreatePage = lazy(() => import('@/pages/brand').then((module) => ({ default: module.BrandCreatePage })));
const BrandEditPage = lazy(() => import('@/pages/brand').then((module) => ({ default: module.BrandEditPage })));
const CategoryPage = lazy(() => import('@/pages/category').then((module) => ({ default: module.CategoryPage })));
const CategoryCreatePage = lazy(() =>
  import('@/pages/category').then((module) => ({ default: module.CategoryCreatePage }))
);
const CategoryEditPage = lazy(() =>
  import('@/pages/category').then((module) => ({ default: module.CategoryEditPage }))
);
const RolePage = lazy(() => import('@/pages/role').then((module) => ({ default: module.RolePage })));
const RoleCreatePage = lazy(() => import('@/pages/role').then((module) => ({ default: module.RoleCreatePage })));
const RoleEditPage = lazy(() => import('@/pages/role').then((module) => ({ default: module.RoleEditPage })));
const UserPage = lazy(() => import('@/pages/user').then((module) => ({ default: module.UserPage })));
const UserCreatePage = lazy(() => import('@/pages/user').then((module) => ({ default: module.UserCreatePage })));
const UserEditPage = lazy(() => import('@/pages/user').then((module) => ({ default: module.UserEditPage })));
const AdminResetCredentialsPage = lazy(() =>
  import('@/pages/user').then((module) => ({ default: module.AdminResetCredentialsPage }))
);
const AgentPage = lazy(() => import('@/pages/agent').then((module) => ({ default: module.AgentPage })));
const AgentCreatePage = lazy(() => import('@/pages/agent').then((module) => ({ default: module.AgentCreatePage })));
const AgentEditPage = lazy(() => import('@/pages/agent').then((module) => ({ default: module.AgentEditPage })));
const AgentShowPage = lazy(() => import('@/pages/agent').then((module) => ({ default: module.AgentShowPage })));
const AgentResetPasswordPage = lazy(() =>
  import('@/pages/agent').then((module) => ({ default: module.AgentResetPasswordPage }))
);
const CourierPage = lazy(() => import('@/pages/courier').then((module) => ({ default: module.CourierPage })));
const CourierCreatePage = lazy(() => import('@/pages/courier').then((module) => ({ default: module.CourierCreatePage })));
const CourierEditPage = lazy(() => import('@/pages/courier').then((module) => ({ default: module.CourierEditPage })));
const CourierResetPasswordPage = lazy(() =>
  import('@/pages/courier').then((module) => ({ default: module.CourierResetPasswordPage }))
);
const CourierTaskPage = lazy(() =>
  import('@/pages/courier-task').then((module) => ({ default: module.CourierTaskPage }))
);
const CourierTaskShowPage = lazy(() =>
  import('@/pages/courier-task').then((module) => ({ default: module.CourierTaskShowPage }))
);
const KassaPage = lazy(() => import('@/pages/kassa').then((module) => ({ default: module.KassaPage })));
const StaffPage = lazy(() => import('@/pages/staff').then((module) => ({ default: module.StaffPage })));
const StaffCreatePage = lazy(() => import('@/pages/staff').then((module) => ({ default: module.StaffCreatePage })));
const StaffEditPage = lazy(() => import('@/pages/staff').then((module) => ({ default: module.StaffEditPage })));
const StaffResetPasswordPage = lazy(() =>
  import('@/pages/staff').then((module) => ({ default: module.StaffResetPasswordPage }))
);
const PackPage = lazy(() => import('@/pages/pack').then((module) => ({ default: module.PackPage })));
const PackCreatePage = lazy(() => import('@/pages/pack').then((module) => ({ default: module.PackCreatePage })));
const PackEditPage = lazy(() => import('@/pages/pack').then((module) => ({ default: module.PackEditPage })));
const ProductPage = lazy(() => import('@/pages/product').then((module) => ({ default: module.ProductPage })));
const ProductCreatePage = lazy(() =>
  import('@/pages/product').then((module) => ({ default: module.ProductCreatePage }))
);
const ProductEditPage = lazy(() =>
  import('@/pages/product').then((module) => ({ default: module.ProductEditPage }))
);
const TaskPage = lazy(() => import('@/pages/task').then((module) => ({ default: module.TaskPage })));
const TaskCreatePage = lazy(() => import('@/pages/task').then((module) => ({ default: module.TaskCreatePage })));
const TaskEditPage = lazy(() => import('@/pages/task').then((module) => ({ default: module.TaskEditPage })));
const WarehousePage = lazy(() => import('@/pages/warehouse').then((module) => ({ default: module.WarehousePage })));
const WarehouseCreatePage = lazy(() =>
  import('@/pages/warehouse').then((module) => ({ default: module.WarehouseCreatePage }))
);
const WarehouseEditPage = lazy(() =>
  import('@/pages/warehouse').then((module) => ({ default: module.WarehouseEditPage }))
);
const PurchasePage = lazy(() => import('@/pages/purchase').then((module) => ({ default: module.PurchasePage })));
const PurchaseCreatePage = lazy(() =>
  import('@/pages/purchase').then((module) => ({ default: module.PurchaseCreatePage }))
);
const OrderPage = lazy(() => import('@/pages/order').then((module) => ({ default: module.OrderPage })));
const OrderShowPage = lazy(() => import('@/pages/order').then((module) => ({ default: module.OrderShowPage })));
const CompanyPage = lazy(() => import('@/pages/company').then((module) => ({ default: module.CompanyPage })));
const CompanyShowPage = lazy(() =>
  import('@/pages/company').then((module) => ({ default: module.CompanyShowPage }))
);
const ApplicationPage = lazy(() =>
  import('@/pages/application').then((module) => ({ default: module.ApplicationPage }))
);
const PartnerPage = lazy(() => import('@/pages/partner').then((module) => ({ default: module.PartnerPage })));
const PartnerCreatePage = lazy(() =>
  import('@/pages/partner').then((module) => ({ default: module.PartnerCreatePage }))
);
const PartnerEditPage = lazy(() =>
  import('@/pages/partner').then((module) => ({ default: module.PartnerEditPage }))
);
const PartnerResetPasswordPage = lazy(() =>
  import('@/pages/partner').then((module) => ({ default: module.PartnerResetPasswordPage }))
);
const PartnerProductsPage = lazy(() =>
  import('@/pages/partner').then((module) => ({ default: module.PartnerProductsPage }))
);
const GeoZonePage = lazy(() => import('@/pages/geo-zone').then((module) => ({ default: module.GeoZonePage })));
const GeoZoneCreatePage = lazy(() =>
  import('@/pages/geo-zone').then((module) => ({ default: module.GeoZoneCreatePage }))
);
const GeoZoneEditPage = lazy(() =>
  import('@/pages/geo-zone').then((module) => ({ default: module.GeoZoneEditPage }))
);
const AgentGeoZonePage = lazy(() =>
  import('@/pages/agent-geo-zone').then((module) => ({ default: module.AgentGeoZonePage }))
);
const AgentGeoZoneCreatePage = lazy(() =>
  import('@/pages/agent-geo-zone').then((module) => ({ default: module.AgentGeoZoneCreatePage }))
);
const AgentGeoZoneEditPage = lazy(() =>
  import('@/pages/agent-geo-zone').then((module) => ({ default: module.AgentGeoZoneEditPage }))
);
const ProfilePage = lazy(() => import('@/pages').then((module) => ({ default: module.ProfilePage })));
const NotFoundPage = lazy(() => import('@/pages/not-found').then((module) => ({ default: module.NotFoundPage })));

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <GuestRoute>
        <LazyWrapper>
          <LoginPage />
        </LazyWrapper>
      </GuestRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <HomePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'agent',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT.INDEX}>
            <LazyWrapper>
              <AgentPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT.CREATE}>
            <LazyWrapper>
              <AgentCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT.UPDATE}>
            <LazyWrapper>
              <AgentEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent/reset-password/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT.RESET_PASSWORD}>
            <LazyWrapper>
              <AgentResetPasswordPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT.SHOW}>
            <LazyWrapper>
              <AgentShowPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'courier',
        element: (
          <PermissionRoute permission={PERMISSIONS.COURIER.INDEX}>
            <LazyWrapper>
              <CourierPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'courier/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.COURIER.CREATE}>
            <LazyWrapper>
              <CourierCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'courier/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.COURIER.UPDATE}>
            <LazyWrapper>
              <CourierEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'courier/reset-password/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.COURIER.RESET_PASSWORD}>
            <LazyWrapper>
              <CourierResetPasswordPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'courier-task',
        element: (
          <PermissionRoute permission={PERMISSIONS.COURIER_TASK.INDEX}>
            <LazyWrapper>
              <CourierTaskPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'courier-task/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.COURIER_TASK.INDEX}>
            <LazyWrapper>
              <CourierTaskShowPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'kassa',
        element: (
          <PermissionRoute permission={PERMISSIONS.KASSA.INDEX}>
            <LazyWrapper>
              <KassaPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'task',
        element: (
          <PermissionRoute permission={PERMISSIONS.TASK.INDEX}>
            <LazyWrapper>
              <TaskPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'task/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.TASK.CREATE}>
            <LazyWrapper>
              <TaskCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'task/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.TASK.UPDATE}>
            <LazyWrapper>
              <TaskEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'staff',
        element: (
          <PermissionRoute permission={PERMISSIONS.STAFF.INDEX}>
            <LazyWrapper>
              <StaffPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'staff/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.STAFF.CREATE}>
            <LazyWrapper>
              <StaffCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'staff/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.STAFF.UPDATE}>
            <LazyWrapper>
              <StaffEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'staff/reset-password/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.STAFF.RESET_PASSWORD}>
            <LazyWrapper>
              <StaffResetPasswordPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'product',
        element: (
          <PermissionRoute permission={PERMISSIONS.PRODUCT.INDEX}>
            <LazyWrapper>
              <ProductPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'product/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.PRODUCT.CREATE}>
            <LazyWrapper>
              <ProductCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'product/edit/:id',
        element: (
          <PermissionRoute
            anyOf={[PERMISSIONS.PRODUCT.UPDATE, PERMISSIONS.PRODUCT.SHOW, PERMISSIONS.PRODUCT.INDEX]}
          >
            <LazyWrapper>
              <ProductEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'pack',
        element: (
          <PermissionRoute permission={PERMISSIONS.PACK.INDEX}>
            <LazyWrapper>
              <PackPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'pack/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.PACK.CREATE}>
            <LazyWrapper>
              <PackCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'pack/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.PACK.UPDATE}>
            <LazyWrapper>
              <PackEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'warehouse',
        element: (
          <PermissionRoute permission={PERMISSIONS.WAREHOUSE.INDEX}>
            <LazyWrapper>
              <WarehousePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'warehouse/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.WAREHOUSE.CREATE}>
            <LazyWrapper>
              <WarehouseCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'warehouse/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.WAREHOUSE.UPDATE}>
            <LazyWrapper>
              <WarehouseEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'purchase',
        element: (
          <PermissionRoute permission={PERMISSIONS.PURCHASE.INDEX}>
            <LazyWrapper>
              <PurchasePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'purchase/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.PURCHASE.CREATE}>
            <LazyWrapper>
              <PurchaseCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'order',
        element: (
          <PermissionRoute permission={PERMISSIONS.ORDER.INDEX}>
            <LazyWrapper>
              <OrderPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'order/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.ORDER.INDEX}>
            <LazyWrapper>
              <OrderShowPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'company',
        element: (
          <PermissionRoute permission={PERMISSIONS.COMPANY.INDEX}>
            <LazyWrapper>
              <CompanyPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'company/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.COMPANY.INDEX}>
            <LazyWrapper>
              <CompanyShowPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'partner',
        element: (
          <PermissionRoute permission={PERMISSIONS.PARTNER.INDEX}>
            <LazyWrapper>
              <PartnerPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'partner/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.PARTNER.CREATE}>
            <LazyWrapper>
              <PartnerCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'partner/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.PARTNER.UPDATE}>
            <LazyWrapper>
              <PartnerEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'partner/reset-password/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.PARTNER.RESET_PASSWORD}>
            <LazyWrapper>
              <PartnerResetPasswordPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'partner/products/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.PARTNER.UPDATE}>
            <LazyWrapper>
              <PartnerProductsPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'application',
        element: (
          <PermissionRoute permission={PERMISSIONS.APPLICATION.INDEX}>
            <LazyWrapper>
              <ApplicationPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'brand',
        element: (
          <PermissionRoute permission={PERMISSIONS.BRAND.INDEX}>
            <LazyWrapper>
              <BrandPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'brand/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.BRAND.CREATE}>
            <LazyWrapper>
              <BrandCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'brand/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.BRAND.UPDATE}>
            <LazyWrapper>
              <BrandEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'category',
        element: (
          <PermissionRoute permission={PERMISSIONS.CATEGORY.INDEX}>
            <LazyWrapper>
              <CategoryPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'category/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.CATEGORY.CREATE}>
            <LazyWrapper>
              <CategoryCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'category/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.CATEGORY.UPDATE}>
            <LazyWrapper>
              <CategoryEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'role',
        element: (
          <PermissionRoute permission={PERMISSIONS.ROLE.INDEX}>
            <LazyWrapper>
              <RolePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'role/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.ROLE.CREATE}>
            <LazyWrapper>
              <RoleCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'role/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.ROLE.UPDATE}>
            <LazyWrapper>
              <RoleEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'user',
        element: (
          <PermissionRoute permission={PERMISSIONS.USER.INDEX}>
            <LazyWrapper>
              <UserPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'user/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.USER.CREATE}>
            <LazyWrapper>
              <UserCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'user/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.USER.UPDATE}>
            <LazyWrapper>
              <UserEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'user/reset-credentials/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.USER.UPDATE_USER_CREDENTIALS}>
            <LazyWrapper>
              <AdminResetCredentialsPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'geo-zone',
        element: (
          <PermissionRoute permission={PERMISSIONS.GEO_ZONE.INDEX}>
            <LazyWrapper>
              <GeoZonePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'geo-zone/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.GEO_ZONE.CREATE}>
            <LazyWrapper>
              <GeoZoneCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'geo-zone/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.GEO_ZONE.UPDATE}>
            <LazyWrapper>
              <GeoZoneEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent-geo-zone',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT_GEO_ZONE.INDEX}>
            <LazyWrapper>
              <AgentGeoZonePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent-geo-zone/create',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT_GEO_ZONE.CREATE}>
            <LazyWrapper>
              <AgentGeoZoneCreatePage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'agent-geo-zone/edit/:id',
        element: (
          <PermissionRoute permission={PERMISSIONS.AGENT_GEO_ZONE.UPDATE}>
            <LazyWrapper>
              <AgentGeoZoneEditPage />
            </LazyWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <LazyWrapper>
            <ProfilePage />
          </LazyWrapper>
        ),
      },
      {
        path: '*',
        element: (
          <LazyWrapper>
            <NotFoundPage />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <LazyWrapper>
        <NotFoundPage />
      </LazyWrapper>
    ),
  },
]);
